// Índice compoundId -> linha do carrinho, memoizado por objeto `families`.
//
// A resolução anterior varria todas as marcas/famílias/produtos até achar a chave,
// montando duas strings por produto. Como ela é chamada uma vez por item do carrinho
// e é reexecutada a cada alteração do orderState (ou seja, a cada tecla digitada numa
// quantidade), o custo era O(itens no carrinho × produtos da vitrine) por tecla — o
// que travava a PDP inteira em vitrines grandes. Agora o índice é construído uma vez
// por objeto `families` (que é estável) e cada resolução é O(1).
const indexCache = new WeakMap();

function buildOrderLineIndex(families) {
  const index = new Map();
  Object.values(families).forEach((brandFamilies) => {
    if (!brandFamilies || typeof brandFamilies !== "object") return;
    Object.entries(brandFamilies).forEach(([famKey, fam]) => {
      if (!fam?.products) return;
      fam.products.forEach((prod) => {
        const baseKey = `${famKey}_${prod.id}`;
        if (!index.has(baseKey)) index.set(baseKey, { famKey, fam, prod });
      });
    });
  });
  return index;
}

export function getOrderLineIndex(families) {
  if (!families || typeof families !== "object") return new Map();
  let index = indexCache.get(families);
  if (!index) {
    index = buildOrderLineIndex(families);
    indexCache.set(families, index);
  }
  return index;
}

/**
 * Resolve a linha a partir do índice. Chaves antigas trazem um sufixo de variante
 * (ex.: `<famKey>_<prodId>_Único`), então, sem correspondência exata, tenta-se o
 * prefixo mais longo removendo segmentos `_` do fim.
 */
export function resolveOrderLineFromIndex(key, index) {
  const exact = index.get(key);
  if (exact) return { ...exact, variant: "" };

  let cut = key.lastIndexOf("_");
  while (cut > 0) {
    const entry = index.get(key.substring(0, cut));
    if (entry) return { ...entry, variant: key.substring(cut + 1) };
    cut = key.lastIndexOf("_", cut - 1);
  }
  return null;
}

/** Resolve a família/produto de uma linha do carrinho a partir da sua compoundId. */
export function resolveOrderLineFromKey(key, families) {
  return resolveOrderLineFromIndex(key, getOrderLineIndex(families));
}
